import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import classNames from "classnames";
import { motion } from "framer-motion";
import qs from "qs";

import AnimatedNumbers from "UI/AnimatedNumbers";

import useResizeObserver from "Utilities/Hooks/useResizeObserver";
import { formatNumberWithCommas } from "Utilities/Number/formatNumberWithCommas";

import { usePaymentForm } from "../PaymentFormContext";

import { useSetAmountToComplete } from "./useSetAmountToComplete";

const AmountInput = forwardRef(
  (
    {
      isExpanded,
      handleNextClick,
      suggestionButtonAmount,
      setSuggestionButtonAmount,
      runValidations,
      showNextButton,
    },
    inputRef
  ) => {
    const {
      state: { amount },
      dispatch,
    } = usePaymentPage();

    const {
      state: { isSubmitting },
      dispatch: paymentFormDispatch,
    } = usePaymentForm();

    const location = useLocation();
    const {
      pledgeID: pledgeIDParam,
      signupListID,
      isSuggestedDonation,
    } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    const pledgeAmount =
      window?.data?.paymentPageData?.campaign?.pledge?.amount;

    const setAmountToComplete = useSetAmountToComplete();

    const [hasAnimated, setHasAnimated] = useState(false);

    const isLongClassName = classNames({
      isLongNumber:
        String(amount).replace("$", "").replace(",", "").replace(".", "")
          .length > 4,
    });

    const handleAmountChange = useCallback(
      (value) => {
        if (value?.replace("$", "").replace(",", "") * 1 > 10000) return;
        if (value === ".") value = "0";

        dispatch({
          type: "setAmount",
          payload: value,
        });
      },
      [dispatch]
    );

    const suggestedAnimationComplete = () => {
      dispatch({
        type: "setAmount",
        payload: suggestionButtonAmount,
      });
    };

    useEffect(() => {
      if (
        suggestionButtonAmount &&
        suggestionButtonAmount > 2 &&
        amount &&
        suggestionButtonAmount === amount
      ) {
        setSuggestionButtonAmount(null);
        handleNextClick();
      }
    }, [
      amount,
      suggestionButtonAmount,
      handleNextClick,
      setSuggestionButtonAmount,
    ]);

    useEffect(
      function setDefaultAmountToPledgeAmount() {
        if (!pledgeAmount || !pledgeIDParam) return;

        handleAmountChange(pledgeAmount);
        paymentFormDispatch({
          type: "setFormStage",
          stage: "amount",
          value: "complete",
        });
      },
      [pledgeAmount, handleAmountChange, paymentFormDispatch, pledgeIDParam]
    );

    const layoutAnimationComplete = () => {
      if (!isExpanded) setTimeout(() => setHasAnimated(true), 500);
    };

    const checkToRunValidations = (e) => {
      // if the user has already selected a suggested amount, then changes it, run validations
      if (hasAnimated) {
        const isValid = runValidations();
        if (isValid) {
          setAmountToComplete();
        } else {
          inputRef.current.focus();
        }
        return;
      }

      // if the blur occurs because the user clicks a button(suggested amount, pay securely), eject and let the button click event handle things
      if (e.relatedTarget?.nodeName === "BUTTON") {
        return;
      }

      // For logged in users who have a card on file, setAmountToComplete will ultimately animate the value to the left and hide the suggested values
      // showNextButton is false essentially when the user is logged in and has a card on file.
      if (amount) {
        const isValid = runValidations();
        if (isValid && !showNextButton) {
          setAmountToComplete();
        } else {
          inputRef.current.focus();
        }
        return;
      }

      /**
       * If none of the above conditions apply, then the 'Next' button should be displayed in AmountSection
       * The user will be required to either select a suggested amount
       * or enter an amount and press the Next button
       */
    };

    const inputKeyUp = (e) => {
      if (e.key === "Enter") {
        inputRef?.current?.blur();
        handleNextClick();
      }
    };

    const checkMeasurements = useCallback(() => {
      if (measuringRef.current && inputRef.current)
        setSmallSize(
          measuringRef.current.getBoundingClientRect().right >
            inputRef.current.getBoundingClientRect().right
        );
    }, [inputRef]);

    useEffect(() => {
      if (amount) setHasAnimated(true);
    }, []);

    useEffect(() => {
      checkMeasurements();
    }, [amount, checkMeasurements]);

    const [smallSize, setSmallSize] = useState(false);

    const measuringRef = useRef();

    useResizeObserver(inputRef, checkMeasurements);

    return (
      <Wrapper className={classNames({ smallSize })}>
        <AmountTextHolder
          style={{ justifyContent: isExpanded ? "center" : "flex-start" }}
        >
          <MeasureSize ref={measuringRef}>
            $
            {(suggestionButtonAmount &&
              formatNumberWithCommas(suggestionButtonAmount)) ||
              (amount && formatNumberWithCommas(amount))}
          </MeasureSize>
          {!hasAnimated && (
            <motion.div
              layout={!hasAnimated}
              onLayoutAnimationComplete={layoutAnimationComplete}
            >
              $
              {(suggestionButtonAmount &&
                formatNumberWithCommas(suggestionButtonAmount)) ||
                (amount && formatNumberWithCommas(amount))}
            </motion.div>
          )}
        </AmountTextHolder>

        {(isExpanded || hasAnimated) && (
          <AmountContent>
            {suggestionButtonAmount && suggestionButtonAmount !== amount ? (
              <NumberStyle>
                <AnimatedNumbers
                  number={suggestionButtonAmount}
                  onAnimationComplete={suggestedAnimationComplete}
                />
              </NumberStyle>
            ) : (
              <>
                {hasAnimated && !amount && (
                  <DollarSignPlaceholder>$</DollarSignPlaceholder>
                )}
                <MaskedInput
                  data-cy="fund-it-giving-input-amount"
                  className={isLongClassName}
                  placeholder={hasAnimated ? "0" : "$0"}
                  prefix="$"
                  allowNegativeValue={false}
                  decimalsLimit={2}
                  maxLength={8}
                  value={amount}
                  onValueChange={handleAmountChange}
                  ref={inputRef}
                  onBlur={checkToRunValidations}
                  style={{ textAlign: hasAnimated ? "left" : "center" }}
                  onKeyUp={inputKeyUp}
                  disabled={
                    isSubmitting || (signupListID && !isSuggestedDonation)
                  }
                />
              </>
            )}
          </AmountContent>
        )}
        <Padding />
      </Wrapper>
    );
  }
);

AmountInput.displayName = "AmountInput";
export default AmountInput;

const MeasureSize = styled.div`
  position: absolute;
  font-size: 50px;
  line-height: 50px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-family: Montserrat;
  opacity: 0;
  visibility: hidden;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const AmountContent = styled.div`
  position: absolute;
  z-index: 5;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 20px);
`;

const MaskedInput = styled(CurrencyInput)`
  box-sizing: border-box;
  width: 100%;
  font-size: 50px;
  line-height: 50px;
  height: 62px;
  font-weight: 600;
  color: #474747;
  border-radius: 10px;
  background: transparent;
  border: none;
  font-variant-numeric: tabular-nums;
  font-family: Montserrat;
  appearance: none;
  padding: 0;
  margin: 0;

  transition: font-size 0.2s;
  //prettier-ignore
  ${Wrapper}.smallSize & {
    font-size: 40px;
    @media screen and (max-width:390px) {
      font-size:34px;
    }
    @media screen and (max-width:360px) {
      font-size:30px;
    }
    @media screen and (max-width:335px) {
      font-size:28px;
    }
  }

  &::-moz-placeholder {
    color: #9a9a9a;
    opacity: 1;
  }
  &::placeholder {
    color: #9a9a9a;
  }
  &:focus {
    outline: 0;
  }
`;

const NumberStyle = styled.div`
  font-size: 50px;
  line-height: 50px;
  font-weight: 600;
  color: #474747;
  font-family: Montserrat;
  height: 50px;
  font-variant-numeric: tabular-nums;

  transition: font-size 0.2s;
  //prettier-ignore
  ${Wrapper}.smallSize & {
    font-size: 40px;
    @media screen and (max-width:390px) {
      font-size:34px;
    }
    @media screen and (max-width:360px) {
      font-size:30px;
    }
    @media screen and (max-width:335px) {
      font-size:28px;
    }
  }
`;
const AmountTextHolder = styled.div`
  font-size: 50px;
  line-height: 50px;
  font-weight: 600;
  color: #474747;
  font-family: Montserrat;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  z-index: 1;
  padding: 0 6px;
  font-variant-numeric: tabular-nums;

  transition: font-size 0.2s;
  //prettier-ignore
  ${Wrapper}.smallSize & {
    font-size: 40px;
    @media screen and (max-width:390px) {
      font-size:34px;
    }
    @media screen and (max-width:360px) {
      font-size:30px;
    }
    @media screen and (max-width:335px) {
      font-size:28px;
    }
  }
`;

const Padding = styled.div`
  height: 14px;
`;

const DollarSignPlaceholder = styled.div`
  font-size: 50px;
  line-height: 50px;
  font-weight: 600;
  color: #9a9a9a;
  font-family: Montserrat;
  font-variant-numeric: tabular-nums;
  //desktop safari only
  @supports selector(:nth-child(1 of x)) {
    @media screen and (hover: hover) and (pointer: fine) and (-webkit-min-device-pixel-ratio: 2) {
      transform: translateY(0.5px);
    }
  }

  transition: font-size 0.2s;
  //prettier-ignore
  ${Wrapper}.smallSize & {
    font-size: 40px;
    @media screen and (max-width:390px) {
      font-size:34px;
    }
    @media screen and (max-width:360px) {
      font-size:30px;
    }
    @media screen and (max-width:335px) {
      font-size:28px;
    }
  }
`;
